import { useState, useCallback, useEffect, useRef } from 'react';
import { getProgressPercent, getProgressMessage } from '../utils/updateProgress';

export type AnimateActionType = 
    | 'animate' 
    | 'remove_background' 
    | 'crop_transparent' 
    | 'export' 
    | 'split_frames';

export const useAnimateActions = () => {
    const [currentAction, setCurrentAction] = useState<AnimateActionType | null>(null);
    const [progress, setProgress] = useState<number>(0);
    const [message, setMessage] = useState<string>('');
    const cleanupRef = useRef<(() => void) | null>(null);

    const startAction = useCallback((action: AnimateActionType) => {
        // Cleanup any existing progress tracking
        if (cleanupRef.current) {
            cleanupRef.current();
        }

        // console.log(`Action started: ${action}`);
        setCurrentAction(action);
        setProgress(0);

        // Start the progress interpolation
        cleanupRef.current = getProgressPercent(action, (nextProgress) => {
            // console.log(`Progress for ${action}: ${nextProgress}%`);
            setProgress(nextProgress);
            const nextMessage = getProgressMessage(action, nextProgress);
            setMessage(nextMessage);
        });
    }, []);

    const stopAction = useCallback(() => {
        if (cleanupRef.current) {
            cleanupRef.current();
            cleanupRef.current = null;
        }
        setCurrentAction((prevAction) => {
            // console.log(`Action stopped: ${prevAction}`);
            return null;
        });
        setProgress(0);
        setMessage('');
    }, []);
    
    // Helper to check if ANY action is running
    const isDoingAction = currentAction !== null;
    
    // Helper to check specific action
    const isAction = useCallback((action: AnimateActionType) => {
        return currentAction === action;
    }, [currentAction]);

    // Ensure cleanup on unmount
    useEffect(() => {
        return () => {
            if (cleanupRef.current) {
                cleanupRef.current();
            }
        };
    }, []);

    return {
        currentAction,
        isDoingAction,
        isAction, 
        startAction,
        stopAction,
        progress,
        message,
    };
};